import React from "react";
import PageHero from "../components/shared/PageHero";
import SectionHeading from "../components/shared/SectionHeading";
import SectionDivider from "../components/decor/SectionDivider";
import CTASection from "../components/shared/CTASection";
import Mandala from "../components/decor/Mandala";
import { IMG } from "../data/site";
import { Quote, BookOpen, Landmark, Star, ScrollText, Users } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const life = [
  {
    year: "1238",
    title: { en: "Divine birth at Pajaka Kshetra", kn: "ಪಾಜಕ ಕ್ಷೇತ್ರದಲ್ಲಿ ದೈವಿಕ ಜನನ" },
    text: {
      en: "Madhyageha Bhatta and Vedavati welcome a divine son - later renowned as Sri Madhva.",
      kn: "ಮಧ್ಯಗೇಹ ಭಟ್ಟ ಮತ್ತು ವೇದವತಿ ದಂಪತಿಗಳ ಮನೆಯಲ್ಲಿ ದೈವಿಕ ಪುತ್ರನಾಗಿ ಜನಿಸಿ, ಮುಂದೆ ಶ್ರೀ ಮಧ್ವರೆಂದು ಪ್ರಸಿದ್ಧರಾಗುತ್ತಾರೆ.",
    },
  },
  {
    year: "1246",
    title: { en: "Childhood & initiation", kn: "ಬಾಲ್ಯ ಮತ್ತು ಉಪನಯನ" },
    text: {
      en: "As a child, remarkable feats reveal his divinity. He is initiated in the study of the Vedas and philosophical systems.",
      kn: "ಬಾಲಕನಾಗಿದ್ದಾಗಲೇ ಅದ್ಭುತ ಚಟುವಟಿಕೆಗಳಿಂದ ತಮ್ಮ ದೈವಿಕತೆಯನ್ನು ಪ್ರಕಟಪಡಿಸುತ್ತಾರೆ. ವೇದ ಹಾಗೂ ದರ್ಶನ ಶಾಸ್ತ್ರಗಳ ಅಭ್ಯಾಸಕ್ಕೆ ಉಪನಯನ ಸ್ವೀಕರಿಸುತ್ತಾರೆ.",
    },
  },
  {
    year: "1254",
    title: { en: "Sannyasa Deeksha", kn: "ಸನ್ಯಾಸ ದೀಕ್ಷೆ" },
    text: {
      en: "At sixteen he takes sannyasa under Sri Achyutapreksha, becoming Poornaprajna, and later Anandateertha, and Madhva.",
      kn: "ಹದಿನಾರನೇ ವಯಸ್ಸಿನಲ್ಲಿ ಶ್ರೀ ಅಚ್ಯುತಪ್ರೇಕ್ಷರಿಂದ ಸನ್ಯಾಸ ಸ್ವೀಕರಿಸಿ ಪೂರ್ಣಪ್ರಜ್ಞರೆಂಬ ಆಶ್ರಮ ನಾಮ ಪಡೆಯುತ್ತಾರೆ; ಮುಂದೆ ಆನಂದತೀರ್ಥ, ಹಾಗೂ ಮಧ್ವರೆಂದು ಖ್ಯಾತರಾಗುತ್ತಾರೆ.",
    },
  },
  {
    year: "1260-70",
    title: { en: "Composition of the Sarvamoola", kn: "ಸರ್ವಮೂಲ ಗ್ರಂಥ ರಚನೆ" },
    text: {
      en: "He composes commentaries on the Prasthanatrayi, the Bhagavata, and independent texts establishing Dvaita Vedanta.",
      kn: "ಪ್ರಸ್ಥಾನತ್ರಯ, ಶ್ರೀಮದ್ಭಾಗವತ ಹಾಗೂ ಸ್ವತಂತ್ರ ಗ್ರಂಥಗಳಲ್ಲಿ ದ್ವೈತ ವೇದಾಂತವನ್ನು ಸ್ಥಾಪಿಸುವ ಭಾಷ್ಯಗಳನ್ನು ರಚಿಸುತ್ತಾರೆ.",
    },
  },
  {
    year: "1275",
    title: { en: "Pilgrimages to Badari", kn: "ಬದರಿ ಯಾತ್ರೆಗಳು" },
    text: {
      en: "Twice visits Badari, receives darshana of Sri Vedavyasa and returns with sacred idols and blessings.",
      kn: "ಎರಡು ಬಾರಿ ಬದರಿಗೆ ಯಾತ್ರೆ ಮಾಡಿ ಶ್ರೀ ವೇದವ್ಯಾಸರ ದರ್ಶನ ಪಡೆದು, ಪವಿತ್ರ ಮೂರ್ತಿಗಳು ಮತ್ತು ಆಶೀರ್ವಾದದೊಂದಿಗೆ ಹಿಂತಿರುಗುತ್ತಾರೆ.",
    },
  },
  {
    year: "1285",
    title: { en: "Consecration of Sri Krishna at Udupi", kn: "ಉಡುಪಿಯಲ್ಲಿ ಶ್ರೀ ಕೃಷ್ಣ ಪ್ರತಿಷ್ಠಾಪನೆ" },
    text: {
      en: "Discovering the divine idol of Sri Krishna, he consecrates it at Udupi and founds the eight Ashta Mathas.",
      kn: "ದೈವಿಕ ಶ್ರೀ ಕೃಷ್ಣ ಮೂರ್ತಿಯನ್ನು ಪಡೆದು ಉಡುಪಿಯಲ್ಲಿ ಪ್ರತಿಷ್ಠಾಪಿಸಿ, ಎಂಟು ಅಷ್ಟಮಠಗಳನ್ನು ಸ್ಥಾಪಿಸುತ್ತಾರೆ.",
    },
  },
  {
    year: "1317",
    title: { en: "Ascension to Badari", kn: "ಬದರಿ ಪ್ರವೇಶ" },
    text: {
      en: "He returns to Badari to be with Sri Vedavyasa, remaining a living presence for his tradition.",
      kn: "ಶ್ರೀ ವೇದವ್ಯಾಸರೊಂದಿಗೆ ಇರಲು ಬದರಿಗೆ ತೆರಳಿ, ತಮ್ಮ ಪರಂಪರೆಗೆ ಜೀವಂತ ಸಾನ್ನಿಧ್ಯವಾಗಿ ಉಳಿಯುತ್ತಾರೆ.",
    },
  },
];

const contributions = [
  {
    icon: Landmark,
    title: { en: "Dvaita Vedanta", kn: "ದ್ವೈತ ವೇದಾಂತ" },
    text: {
      en: "Established the philosophy of eternal distinction between the individual soul, matter and the Supreme Lord - Sri Vishnu.",
      kn: "ಜೀವ, ಜಗತ್ತು ಮತ್ತು ಪರಮಾತ್ಮ ಶ್ರೀ ವಿಷ್ಣುವಿನ ಶಾಶ್ವತ ಭೇದದ ದರ್ಶನವನ್ನು ಸ್ಥಾಪಿಸಿದರು.",
    },
  },
  {
    icon: Users,
    title: { en: "Ashta Mathas", kn: "ಅಷ್ಟ ಮಠಗಳು" },
    text: {
      en: "Founded eight sacred Mathas at Udupi, entrusted with the worship of Sri Krishna in an eternal Paryaya rotation.",
      kn: "ಉಡುಪಿಯಲ್ಲಿ ಎಂಟು ಪವಿತ್ರ ಮಠಗಳನ್ನು ಸ್ಥಾಪಿಸಿ, ಶ್ರೀ ಕೃಷ್ಣನ ಶಾಶ್ವತ ಪರ್ಯಾಯ ಆವರ್ತನ ಪೂಜೆಯನ್ನು ಅವರಿಗೆ ಒಪ್ಪಿಸಿದರು.",
    },
  },
  {
    icon: ScrollText,
    title: { en: "Sarvamoola Granthas", kn: "ಸರ್ವಮೂಲ ಗ್ರಂಥಗಳು" },
    text: {
      en: "Composed 37 philosophical works including commentaries on the Brahma Sutras, Bhagavad Geeta, Upanishads, and Bhagavata.",
      kn: "ಬ್ರಹ್ಮಸೂತ್ರ, ಭಗವದ್ಗೀತೆ, ಉಪನಿಷತ್ತು ಹಾಗೂ ಭಾಗವತಗಳ ಭಾಷ್ಯಗಳನ್ನೊಳಗೊಂಡು ೩೭ ದಾರ್ಶನಿಕ ಕೃತಿಗಳನ್ನು ರಚಿಸಿದರು.",
    },
  },
  {
    icon: BookOpen,
    title: { en: "Vedic Revival", kn: "ವೈದಿಕ ಪುನರುಜ್ಜೀವನ" },
    text: {
      en: "Revitalised Vedic worship, the study of Nyaya, Mimamsa, and the tradition of Vaishnava bhakti.",
      kn: "ವೈದಿಕ ಪೂಜೆ, ನ್ಯಾಯ-ಮೀಮಾಂಸೆಗಳ ಅಧ್ಯಯನ, ವೈಷ್ಣವ ಭಕ್ತಿ ಪರಂಪರೆಯನ್ನು ಪುನರುಜ್ಜೀವಿಸಿದರು.",
    },
  },
  {
    icon: Star,
    title: { en: "Spiritual Leadership", kn: "ಆಧ್ಯಾತ್ಮಿಕ ನಾಯಕತ್ವ" },
    text: {
      en: "Trained direct disciples who carried the tradition forward - a legacy that flourishes seven centuries later.",
      kn: "ನೇರ ಶಿಷ್ಯರನ್ನು ತಯಾರುಗೊಳಿಸಿ ಪರಂಪರೆಯನ್ನು ಮುಂದೊಯ್ದವರು - ಈ ಪರಂಪರೆ ಏಳು ಶತಮಾನಗಳ ನಂತರವೂ ಪ್ರಜ್ವಲಿಸುತ್ತಿದೆ.",
    },
  },
  {
    icon: Star,
    title: { en: "Sri Krishna Pratishtha at Udupi", kn: "ಉಡುಪಿಯಲ್ಲಿ ಶ್ರೀ ಕೃಷ್ಣ ಪ್ರತಿಷ್ಠೆ" },
    text: {
      en: "Consecrated the celebrated idol of Sri Krishna at Udupi - a self-manifest divine form retrieved from the ocean - and instituted its daily worship in the eternal Paryaya tradition observed to this day.",
      kn: "ಸಮುದ್ರದಿಂದ ದೊರೆತ ಸ್ವಯಂ ಪ್ರಕಟ ದೈವಿಕ ಸ್ವರೂಪದ ಪ್ರಸಿದ್ಧ ಶ್ರೀ ಕೃಷ್ಣ ಮೂರ್ತಿಯನ್ನು ಉಡುಪಿಯಲ್ಲಿ ಪ್ರತಿಷ್ಠಾಪಿಸಿ, ಇಂದಿಗೂ ಪಾಲಿಸಲ್ಪಡುತ್ತಿರುವ ಶಾಶ್ವತ ಪರ್ಯಾಯ ಪರಂಪರೆಯಲ್ಲಿ ದೈನಂದಿನ ಪೂಜೆಯನ್ನು ಸ್ಥಾಪಿಸಿದರು.",
    },
  },
];

const teachings = [
  {
    skt: "हरिः सर्वोत्तमः",
    en: "Hari is the Supreme.",
    kn: "ಹರಿಯೇ ಸರ್ವೋತ್ತಮ.",
    note: {
      en: "The foundational principle of Dvaita - the Supreme Lord is unmatched, unrivaled, and eternally distinct.",
      kn: "ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಮೂಲ ತತ್ವ - ಪರಮಾತ್ಮನು ಸಮಾನನಿಲ್ಲದ, ಅಪ್ರತಿಮ, ಶಾಶ್ವತ ಭಿನ್ನನಾದವನು.",
    },
  },
  {
    skt: "भेदः जीव-जगत्-ईश्वरयोः",
    en: "Real is the distinction between soul, world, and God.",
    kn: "ಜೀವ, ಜಗತ್ತು ಹಾಗೂ ಈಶ್ವರರ ಭೇದ ಸತ್ಯ.",
    note: {
      en: "Dvaita affirms the reality of the world and the eternal difference between souls and the Supreme.",
      kn: "ದ್ವೈತ ಸಿದ್ಧಾಂತವು ಜಗತ್ತಿನ ವಾಸ್ತವಿಕತೆ ಹಾಗೂ ಜೀವ-ಪರಮಾತ್ಮ ನಡುವಿನ ಶಾಶ್ವತ ಭೇದವನ್ನು ದೃಢಪಡಿಸುತ್ತದೆ.",
    },
  },
  {
    skt: "भक्तिः मुक्तिसाधनम्",
    en: "Devotion is the means to liberation.",
    kn: "ಭಕ್ತಿಯೇ ಮುಕ್ತಿಯ ಸಾಧನ.",
    note: {
      en: "Bhakti - devotion to Sri Vishnu with knowledge - is the direct path to moksha.",
      kn: "ಜ್ಞಾನ ಸಹಿತವಾದ ಶ್ರೀ ವಿಷ್ಣುವಿನ ಭಕ್ತಿಯೇ ಮೋಕ್ಷದ ನೇರ ಮಾರ್ಗ.",
    },
  },
];

const COPY = {
  hero: {
    eyebrow: { en: "Jagadguru", kn: "ಜಗದ್ಗುರು" },
    title: { en: "Sri Madhvacharya - founder of Dvaita Vedanta.", kn: "ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯ - ದ್ವೈತ ವೇದಾಂತದ ಸ್ಥಾಪಕರು." },
    subtitle: {
      en: "The luminous acharya whose teachings inspired the Ashta Mathas and continues to guide millions of devotees.",
      kn: "ಅಷ್ಟ ಮಠಗಳಿಗೆ ಸ್ಫೂರ್ತಿಯಾದ, ಕೋಟಿ ಕೋಟಿ ಭಕ್ತರಿಗೆ ಮಾರ್ಗದರ್ಶನ ಮಾಡುತ್ತಿರುವ ದೀಪ್ತಿಮಂತ ಆಚಾರ್ಯ.",
    },
  },
  portrait: {
    eyebrow: { en: "Portrait", kn: "ಚಿತ್ರಪಟ" },
    h: { en: "Jagadguru Sri Madhvacharya", kn: "ಜಗದ್ಗುರು ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯ" },
    body: {
      en: "Sri Madhvacharya (1238-1317 CE), also known as Sri Purnaprajna and Sri Anandatirtha, is revered as the founder of the Dvaita school of Vedanta. He is regarded by the tradition as the third avatar of Vayu, after Sri Hanuman and Sri Bhima, and is celebrated for his extensive philosophical works, the Sarvamoola Granthas, and for establishing the eight Ashta Mathas of Udupi.",
      kn: "ಶ್ರೀ ಮಧ್ವಾಚಾರ್ಯರು (ಕ್ರಿ.ಶ. ೧೨೩೮-೧೩೧೭), ಶ್ರೀ ಪೂರ್ಣಪ್ರಜ್ಞರೆಂದೂ ಶ್ರೀ ಆನಂದತೀರ್ಥರೆಂದೂ ಪ್ರಸಿದ್ಧರಾದವರು, ದ್ವೈತ ವೇದಾಂತ ಪರಂಪರೆಯ ಸ್ಥಾಪಕರೆಂದು ಗೌರವಿಸಲ್ಪಡುತ್ತಾರೆ. ಪರಂಪರೆಯಲ್ಲಿ ಶ್ರೀ ಹನುಮಂತ ಹಾಗೂ ಶ್ರೀ ಭೀಮಸೇನರ ನಂತರ ವಾಯುದೇವರ ಮೂರನೆಯ ಅವತಾರವಾಗಿ ಪೂಜಿಸಲ್ಪಡುತ್ತಾರೆ. ವ್ಯಾಪಕ ದಾರ್ಶನಿಕ ಗ್ರಂಥಗಳಾದ ಸರ್ವಮೂಲ ಗ್ರಂಥಗಳ ರಚನೆ, ಉಡುಪಿಯ ಎಂಟು ಅಷ್ಟ ಮಠಗಳ ಸ್ಥಾಪನೆಗಾಗಿ ಇವರು ಪ್ರಸಿದ್ಧರಾಗಿದ್ದಾರೆ.",
    },
  },
  lifeH: { en: "Life Journey", kn: "ಜೀವನ ಪ್ರವಾಸ" },
  lifeT: { en: "A luminous life across the 13th century", kn: "೧೩ನೇ ಶತಮಾನದಾದ್ಯಂತದ ಬೆಳಗುತ್ತಿರುವ ಜೀವನ" },
  contribH: { en: "Contributions", kn: "ಕೊಡುಗೆಗಳು" },
  contribT: { en: "What Sri Madhva gave the world", kn: "ಶ್ರೀ ಮಧ್ವರು ಜಗತ್ತಿಗೆ ನೀಡಿದ ಕೊಡುಗೆ" },
  teachH: { en: "Teachings", kn: "ಬೋಧನೆಗಳು" },
  teachT: { en: "Timeless principles of Dvaita", kn: "ದ್ವೈತ ಸಿದ್ಧಾಂತದ ಶಾಶ್ವತ ತತ್ವಗಳು" },
  ctaEye: { en: "Continue", kn: "ಮುಂದುವರಿಸಿ" },
  ctaT: { en: "Discover the luminary of Sode - Sri Vadiraja Theertha", kn: "ಸೋದೆಯ ಜ್ಯೋತಿಯನ್ನು ಅನ್ವೇಷಿಸಿ - ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥ" },
  ctaP: { en: "Sri Vadiraja Theertha", kn: "ಶ್ರೀ ವಾದಿರಾಜ ತೀರ್ಥ" },
  ctaS: { en: "Guru Parampara", kn: "ಗುರುಪರಂಪರೆ" },
};

const Madhvacharya = () => {
  const { lang } = useLang();
  const L = (o) => (o && o[lang]) || (o && o.en) || "";
  return (
    <>
      <PageHero
        eyebrow={L(COPY.hero.eyebrow)}
        title={L(COPY.hero.title)}
        subtitle={L(COPY.hero.subtitle)}
      />

      {/* Portrait */}
      <section className="py-16 sm:py-20" data-testid="madhva-portrait">
        <div className="max-w-5xl mx-auto px-4 lg:px-8 grid md:grid-cols-2 gap-10 items-center">
          <div className="relative">
            <div className="aspect-[3/4] rounded-3xl overflow-hidden shadow-2xl border-4 border-[#D4AF37]/30">
              <img
                src={require("../assets/bqf7tgll_srimadhvacharya_hires.png")}
                alt="Sri Madhvacharya"
                className="w-full h-full object-cover"
                data-testid="madhva-image"
              />
            </div>
            <div className="absolute -top-6 -left-6 opacity-70"><Mandala size={140} color="#D4AF37" opacity={0.6} /></div>
          </div>
          <div>
            <div className="overline text-[#B65A14] mb-3">{L(COPY.portrait.eyebrow)}</div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#3B2A1A] leading-tight">{L(COPY.portrait.h)}</h2>
            <div className="mt-3 gold-underline" />
            <p className="mt-5 text-[#3B2A1A]/85 leading-relaxed">{L(COPY.portrait.body)}</p>
          </div>
        </div>
      </section>

      {/* Life journey */}
      <section className="py-16" data-testid="madhva-life">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={L(COPY.lifeH)} title={L(COPY.lifeT)} align="center" />
          <div className="mt-16 relative">
            <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-gradient-to-b from-transparent via-[#D4AF37] to-transparent" />
            <div className="space-y-10">
              {life.map((t, i) => (
                <div key={i} className={`relative grid md:grid-cols-2 gap-6 md:gap-14 ${i % 2 ? "md:[&>*:first-child]:order-2" : ""}`}>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "md:text-left" : "md:text-right"}`}>
                    <div className="overline text-[#B65A14] mb-2">{t.year}</div>
                    <h3 className="font-serif-display text-2xl text-[#3B2A1A]">{L(t.title)}</h3>
                  </div>
                  <div className={`pl-12 md:pl-0 ${i % 2 ? "" : "md:pl-12"}`}>
                    <p className="text-[#3B2A1A]/80 leading-relaxed">{L(t.text)}</p>
                  </div>
                  <div className="absolute left-4 md:left-1/2 -translate-x-1/2 top-1 w-4 h-4 rounded-full bg-[#D4AF37] ring-4 ring-[#FBF7F0]" />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <SectionDivider variant="lotus" />

      {/* Contributions */}
      <section className="bg-sand-texture py-16 relative overflow-hidden" data-testid="madhva-contributions">
        <div className="absolute -top-24 -right-24 opacity-30 animate-mandala-slow"><Mandala size={340} color="#B65A14" opacity={0.5} /></div>
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={L(COPY.contribH)} title={L(COPY.contribT)} align="center" />
          <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {contributions.map((c, idx) => (
              <div key={idx} className="bg-[#FBF7F0] border border-[#D4AF37]/25 rounded-2xl p-8 card-lift" data-testid={`madhva-contrib-${idx}`}>
                <div className="w-12 h-12 rounded-xl bg-[#B65A14] text-[#D4AF37] flex items-center justify-center mb-5">
                  <c.icon className="w-5 h-5" />
                </div>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mb-2">{L(c.title)}</h3>
                <p className="text-sm text-[#3B2A1A]/80 leading-relaxed">{L(c.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Teachings */}
      <section className="py-16" data-testid="madhva-teachings">
        <div className="max-w-6xl mx-auto px-4 lg:px-8">
          <SectionHeading eyebrow={L(COPY.teachH)} title={L(COPY.teachT)} align="center" />
          <div className="mt-14 grid md:grid-cols-3 gap-6">
            {teachings.map((tt) => (
              <div key={tt.skt} className="relative bg-gradient-to-br from-[#FBF7F0] to-[#F2E7D5]/60 border border-[#D4AF37]/40 rounded-2xl p-8 text-center card-lift" data-testid={`madhva-teaching-${tt.en.slice(0, 12)}`}>
                <Quote className="w-6 h-6 text-[#D4AF37] mx-auto mb-4" />
                <div className="font-serif-display text-2xl text-[#B65A14] mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>{tt.skt}</div>
                <div className="font-serif-display italic text-lg text-[#3B2A1A] mb-3">{`"${lang === "kn" ? tt.kn : tt.en}"`}</div>
                <div className="text-sm text-[#3B2A1A]/75 leading-relaxed">{L(tt.note)}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection
        eyebrow={L(COPY.ctaEye)}
        title={L(COPY.ctaT)}
        primary={{ label: L(COPY.ctaP), to: "/lineage?tab=vadiraja" }}
        secondary={{ label: L(COPY.ctaS), to: "/lineage?tab=guru" }}
        image={IMG.templeInterior}
      />
    </>
  );
};

export const MadhvacharyaContent = Madhvacharya;
export default Madhvacharya;
